import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Check, LogOut, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { auth, googleProvider } from "@/lib/firebase";
import { getMyRsvp, upsertRsvp, removeRsvp } from "@/lib/rsvpStore";
import { GoldDivider } from "./Ornaments";
import { WEDDING } from "@/constants/testIds";

const GoogleGlyph = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.2 13.3 17.6 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7c4.3-4 6.9-9.9 6.9-17.4z" />
    <path fill="#FBBC05" d="M10.4 28.3c-.5-1.5-.8-3-.8-4.8s.3-3.3.8-4.8l-7.8-6.1C1 15.7 0 19.7 0 23.5s1 7.8 2.6 11.1l7.8-6.3z" />
    <path fill="#34A853" d="M24 47c6.2 0 11.4-2 15.2-5.6l-7.3-5.7c-2 1.4-4.7 2.3-7.9 2.3-6.4 0-11.8-3.8-13.6-9.3l-7.8 6.3C6.5 42.6 14.6 47 24 47z" />
  </svg>
);

// Guests may attend either function or both — at least one is required.
const EVENT_OPTIONS = [
  { key: "marriage", label: "Marriage", testId: "eventMarriage" },
  { key: "reception", label: "Reception", testId: "eventReception" },
];

// A guest who has not responded yet starts with nothing ticked — they must
// make an explicit choice.
const NO_EVENTS = { marriage: false, reception: false };

// RSVPs saved before this field existed predate the choice, so show them as
// "attending both" — matching eventsOf() in rsvpStore, so the form agrees with
// what the admin list and catering CSV already report for that guest.
const LEGACY_EVENTS = { marriage: true, reception: true };

export const Rsvp = () => {
  const [user, setUser] = useState(null);
  const [accepted, setAccepted] = useState(true);
  const [seats, setSeats] = useState(1);
  const [events, setEvents] = useState(NO_EVENTS);
  const [busy, setBusy] = useState(false);
  const [eventsTouched, setEventsTouched] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const mine = await getMyRsvp(u.uid);
          if (mine) {
            setSeats(mine.seats || 1);
            setAccepted(mine.accepted ?? true);
            setEvents({ ...LEGACY_EVENTS, ...(mine.events || {}) });
          }
        } catch (e) {}
      }
    });
    return unsub;
  }, []);

  const signIn = async () => {
    try {
      setBusy(true);
      await signInWithPopup(auth, googleProvider);
      toast.success("Signed in");
    } catch (e) {
      const msg =
        e.code === "auth/unauthorized-domain"
          ? "This domain isn't authorised in Firebase yet."
          : e.code === "auth/popup-closed-by-user"
          ? "Sign-in cancelled."
          : e.message;
      toast.error("Sign-in failed", { description: msg });
    } finally {
      setBusy(false);
    }
  };

  const toggleEvent = (key) => {
    setEventsTouched(true);
    setEvents((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const noEventChosen = accepted && !events.marriage && !events.reception;

  const submit = async () => {
    if (!user) return;
    if (noEventChosen) {
      setEventsTouched(true);
      toast.error("Please choose at least one event", {
        description: "Tick Marriage, Reception, or both.",
      });
      return;
    }
    try {
      setBusy(true);
      await upsertRsvp(user.uid, {
        name: user.displayName || user.email,
        email: user.email,
        seats,
        accepted,
        events,
      });
      toast.success(
        accepted ? `Seats confirmed for ${seats}` : "Marked as unable to attend",
        { description: "Thank you — your response is saved." }
      );
    } catch (e) {
      toast.error("Could not save", { description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const removeSeat = async () => {
    if (!user) return;
    try {
      setBusy(true);
      await removeRsvp(user.uid);
      setAccepted(false);
      setSeats(1);
      setEvents(NO_EVENTS);
      toast("Your seats were removed", { description: "You'll be missed!" });
    } catch (e) {
      toast.error("Could not remove", { description: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="rsvp" className="relative bg-[var(--kasavu)] px-6 py-28 md:py-36">
      <div className="mx-auto max-w-xl">
        <div className="text-center">
          <p className="font-body text-[11px] uppercase tracking-mega text-[var(--gold-deep)]">
            Chapter III —{" "}
            <span className="font-malayalam normal-case tracking-normal">സാന്നിധ്യം</span>
          </p>
          <h2 className="mt-4 font-display text-5xl italic text-[var(--maroon)] md:text-7xl">
            Will you join us?
          </h2>
          <p className="mx-auto mt-4 max-w-lg font-body text-sm leading-relaxed text-[var(--muted)]">
            Kindly accept the invitation and let us know how many seats you
            need. Plans change — you can update or remove your seats anytime.
          </p>
          <GoldDivider className="mt-8" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative mt-14 overflow-hidden rounded-3xl border border-gold/40 bg-white p-8 glow-gold md:p-10"
        >
          <span className="absolute left-0 top-0 h-full w-1 bg-[var(--maroon)]" />
          <h3 className="font-display text-3xl italic text-[var(--ink)]">
            Accept Invitation
          </h3>

          {!user ? (
            <div className="mt-8">
              <p className="font-body text-sm text-[var(--muted)]">
                Sign in with Google to confirm your seats.
              </p>
              <button
                data-testid={WEDDING.googleSignin}
                onClick={signIn}
                disabled={busy}
                className="mt-5 flex w-full items-center justify-center gap-3 rounded-full border border-[var(--border)] bg-white px-6 py-3.5 font-body text-sm font-medium text-[var(--ink)] shadow-sm transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <GoogleGlyph />}
                Sign in with Google
              </button>
            </div>
          ) : (
            <div className="mt-8 space-y-7">
              <div className="flex items-center justify-between rounded-2xl bg-[var(--kasavu)] px-4 py-3">
                <div className="flex items-center gap-3">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt="" className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--maroon)] font-display text-lg text-white">
                      {(user.displayName || user.email || "?").charAt(0)}
                    </span>
                  )}
                  <div>
                    <p className="font-body text-sm font-semibold text-[var(--ink)]">
                      {user.displayName || "Guest"}
                    </p>
                    <p className="font-body text-xs text-[var(--muted)]">{user.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => signOut(auth)}
                  className="text-[var(--muted)] transition-colors hover:text-[var(--maroon)]"
                  aria-label="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>

              <button
                data-testid={WEDDING.acceptToggle}
                onClick={() => setAccepted((v) => !v)}
                className={`flex w-full items-center justify-between rounded-2xl border px-5 py-4 transition-colors ${
                  accepted ? "border-[var(--green)] bg-green/10" : "border-[var(--border)] bg-white"
                }`}
              >
                <span className="font-body text-sm font-medium text-[var(--ink)]">
                  Joyfully accepts
                </span>
                <span
                  className={`grid h-6 w-6 place-items-center rounded-full transition-colors ${
                    accepted ? "bg-[var(--green)] text-white" : "bg-ink/20"
                  }`}
                >
                  {accepted && <Check className="h-4 w-4" strokeWidth={3} />}
                </span>
              </button>

              <div className={accepted ? "" : "pointer-events-none opacity-40"}>
                <p className="font-body text-[11px] uppercase tracking-[0.24em] text-[var(--green-deep)]">
                  Which events will you join?{" "}
                  <span className="normal-case tracking-normal text-[var(--muted)]">
                    (choose at least one)
                  </span>
                </p>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {EVENT_OPTIONS.map(({ key, label, testId }) => (
                    <button
                      key={key}
                      type="button"
                      role="checkbox"
                      aria-checked={events[key]}
                      data-testid={WEDDING[testId]}
                      onClick={() => toggleEvent(key)}
                      className={`flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-colors ${
                        events[key]
                          ? "border-[var(--green)] bg-green/10"
                          : "border-[var(--border)] bg-white"
                      }`}
                    >
                      <span
                        className={`grid h-5 w-5 shrink-0 place-items-center rounded-md transition-colors ${
                          events[key] ? "bg-[var(--green)] text-white" : "bg-ink/20"
                        }`}
                      >
                        {events[key] && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                      </span>
                      <span className="font-body text-sm font-medium text-[var(--ink)]">
                        {label}
                      </span>
                    </button>
                  ))}
                </div>
                {eventsTouched && noEventChosen && (
                  <p className="mt-2 font-body text-xs text-[var(--maroon)]">
                    Please choose at least one event.
                  </p>
                )}
              </div>

              <div className={accepted ? "" : "pointer-events-none opacity-40"}>
                <p className="font-body text-[11px] uppercase tracking-[0.24em] text-[var(--green-deep)]">
                  Number of seats
                </p>
                <div className="mt-3 flex items-center gap-5">
                  <button
                    data-testid={WEDDING.seatDecrement}
                    onClick={() => setSeats((s) => Math.max(1, s - 1))}
                    className="grid h-12 w-12 place-items-center rounded-full border border-[var(--gold)] text-[var(--maroon)] transition-colors hover:bg-[var(--gold)]"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                  <span
                    data-testid={WEDDING.seatCount}
                    className="w-16 text-center font-display text-5xl italic text-[var(--ink)]"
                  >
                    {seats}
                  </span>
                  <button
                    data-testid={WEDDING.seatIncrement}
                    onClick={() => setSeats((s) => Math.min(20, s + 1))}
                    className="grid h-12 w-12 place-items-center rounded-full border border-[var(--gold)] text-[var(--maroon)] transition-colors hover:bg-[var(--gold)]"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  data-testid={WEDDING.rsvpSubmit}
                  onClick={submit}
                  disabled={busy || noEventChosen}
                  className="flex-1 rounded-full bg-[var(--maroon)] px-6 py-3.5 font-body text-sm uppercase tracking-[0.18em] text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                >
                  {accepted ? "Confirm seats" : "Update RSVP"}
                </button>
                <button
                  data-testid={WEDDING.rsvpRemove}
                  onClick={removeSeat}
                  disabled={busy}
                  className="flex items-center justify-center gap-2 rounded-full border border-[var(--border)] px-6 py-3.5 font-body text-sm text-[var(--muted)] transition-colors hover:border-[var(--maroon)] hover:text-[var(--maroon)] disabled:opacity-60"
                >
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
